import { db } from './firebase';
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
  increment,
} from 'firebase/firestore';
import { generatePNR, calculateRefund } from '../utils/helpers';

export const bookingService = {
  // Create a new booking
  createBooking: async (bookingData) => {
    const pnr = generatePNR();
    const bookingRef = doc(db, 'bookings', pnr);

    await setDoc(bookingRef, {
      pnr,
      userId: bookingData.userId,
      trainId: bookingData.trainId,
      passengers: bookingData.passengers, // Array of passenger objects
      seats: bookingData.seats, // Array of {coachNumber, seatNumber}
      totalFare: bookingData.totalFare,
      status: 'confirmed', // confirmed, cancelled, waiting-list
      paymentStatus: 'completed',
      paymentId: bookingData.paymentId || null,
      bookingDate: Timestamp.now(),
      journeyDate: bookingData.journeyDate,
      cancellationDate: null,
      refundAmount: 0,
      notes: '',
    });

    // Update train seat availability
    const trainRef = doc(db, 'trains', bookingData.trainId);
    await updateDoc(trainRef, {
      availableSeats: increment(-bookingData.seats.length),
    });

    // Update user booking count
    const userRef = doc(db, 'users', bookingData.userId);
    await updateDoc(userRef, {
      totalBookings: increment(1),
    });

    return pnr;
  },

  // Get booking by PNR
  getBookingByPnr: async (pnr) => {
    const bookingDoc = await getDoc(doc(db, 'bookings', pnr));
    return bookingDoc.exists() ? { id: bookingDoc.id, ...bookingDoc.data() } : null;
  },

  // Get user bookings
  getUserBookings: async (userId) => {
    const q = query(
      collection(db, 'bookings'),
      where('userId', '==', userId),
      orderBy('bookingDate', 'desc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  },

  // Get bookings for a train
  getTrainBookings: async (trainId) => {
    const q = query(
      collection(db, 'bookings'),
      where('trainId', '==', trainId)
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
    }));
  },

  // Cancel booking
  cancelBooking: async (pnr) => {
    const booking = await bookingService.getBookingByPnr(pnr);
    if (!booking) throw new Error('Booking not found');

    const refundAmount = calculateRefund(booking.totalFare);

    await updateDoc(doc(db, 'bookings', pnr), {
      status: 'cancelled',
      cancellationDate: Timestamp.now(),
      refundAmount,
    });

    // Release seats
    const trainRef = doc(db, 'trains', booking.trainId);
    await updateDoc(trainRef, {
      availableSeats: increment(booking.seats.length),
    });

    return { pnr, refund: refundAmount };
  },

  // Update booking status
  updateBookingStatus: async (pnr, status) => {
    await updateDoc(doc(db, 'bookings', pnr), {
      status,
      updatedAt: Timestamp.now(),
    });
  },

  // Mark passengers as boarded (TTR function)
  markPassengersBoarded: async (pnr, boardedPassengers) => {
    const booking = await bookingService.getBookingByPnr(pnr);
    if (!booking) throw new Error('Booking not found');

    const updatedPassengers = booking.passengers.map(p => ({
      ...p,
      status: boardedPassengers.includes(p.id) ? 'boarded' : p.status,
    }));

    await updateDoc(doc(db, 'bookings', pnr), {
      passengers: updatedPassengers,
      updatedAt: Timestamp.now(),
    });
  },

  // Get booking analytics (Admin)
  getBookingAnalytics: async (startDate, endDate) => {
    const q = query(
      collection(db, 'bookings'),
      where('bookingDate', '>=', startDate),
      where('bookingDate', '<=', endDate)
    );

    const snapshot = await getDocs(q);
    const bookings = snapshot.docs.map(doc => doc.data());

    return {
      totalBookings: bookings.length,
      totalRevenue: bookings.reduce((sum, b) => sum + b.totalFare, 0),
      confirmedBookings: bookings.filter(b => b.status === 'confirmed').length,
      cancelledBookings: bookings.filter(b => b.status === 'cancelled').length,
      waitingListCount: bookings.filter(b => b.status === 'waiting-list').length,
    };
  },
};

export default bookingService;
